import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Popconfirm
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, TagsOutlined,
    SearchOutlined, DeleteOutlined, FilterOutlined, ClearOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Text } = Typography;

const ManageSalesProductCategory = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    // Top filter bar state (server-side search)
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState({});

    /* ─── column search helpers ─────────────────────────────── */
    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => { clearFilters(); setSearchText(''); };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    /* ─── fetch ──────────────────────────────────────────────── */
    const fetchCategories = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/settings/salesproductcategory/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                },
            });
            setCategories((res.data.dataset || []).map(item => ({
                spcId: item.spc_id,
                code: item.code,
                name: item.name,
                createdAt: item.created_at,
                updatedAt: item.updated_at,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch sales product categories.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCategories(1, 10, {}); }, [fetchCategories]);
    const handleTableChange = (pg) => fetchCategories(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = { search: search.trim() };
        setApplied(filters);
        fetchCategories(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setApplied({});
        fetchCategories(1, pagination.pageSize, {});
    };

    /* ─── add ────────────────────────────────────────────────── */
    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/salesproductcategory/add.php', {
                code: values.code,
                name: values.name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category added successfully.");
                setIsAddModalVisible(false);
                fetchCategories(1, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add category.");
        } finally { setLoading(false); }
    };

    /* ─── edit modal ─────────────────────────────────────────── */
    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({ code: record.code, name: record.name });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/salesproductcategory/edit.php', {
                spc_id: currentItem.spcId,
                code: values.code,
                name: values.name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category updated successfully.");
                setIsModalVisible(false);
                fetchCategories(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update category.");
        } finally { setLoading(false); }
    };

    /* ─── delete ─────────────────────────────────────────────── */
    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/salesproductcategory/delete.php', {
                spc_id: record.spcId,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category deleted successfully.");
                fetchCategories(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to delete category.");
        } finally { setLoading(false); }
    };

    /* ─── columns ────────────────────────────────────────────── */
    const columns = [
        {
            title: '#', key: 'index', width: 60,
            render: (_, __, idx) => (pagination.current - 1) * pagination.pageSize + idx + 1,
        },
        {
            title: 'Code', dataIndex: 'code', key: 'code', width: 180,
            sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
            ...getColumnSearchProps('code'),
            render: (val) => <Text code>{val}</Text>,
        },
        {
            title: 'Category Name', dataIndex: 'name', key: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            ...getColumnSearchProps('name'),
        },
        {
            title: 'Last Updated', dataIndex: 'updatedAt', key: 'updatedAt', width: 160, align: 'center',
            render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : <Text type="secondary">—</Text>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 110,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Category">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Popconfirm
                        title="Delete Category"
                        description="This category will be removed permanently."
                        okText="Yes, Delete"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete Category">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    /* ─── render ─────────────────────────────────────────────── */
    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Settings' },
                                { title: 'Sales Product Categories' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <TagsOutlined /> Sales Product Categories
                        </h1>
                        <p className="fluid-page-subtitle">Manage category classifications for sales products</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add New Category
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Code or category name"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={categories}
                            rowKey="spcId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 640 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add New Sales Product Category"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item
                        label="Category Code" name="code"
                        rules={[
                            { required: true, message: 'Please enter a category code' },
                            { max: 50, message: 'Max 50 characters' },
                            { pattern: /^[A-Za-z0-9\-_]+$/, message: 'Only letters, numbers, hyphens and underscores' },
                        ]}
                    >
                        <Input placeholder="e.g. FRT-001" />
                    </Form.Item>
                    <Form.Item
                        label="Category Name" name="name"
                        rules={[
                            { required: true, message: 'Please enter a category name' },
                            { max: 150, message: 'Max 150 characters' },
                        ]}
                    >
                        <Input placeholder="e.g. Fertilizers" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Category</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Sales Product Category"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item
                        label="Category Code" name="code"
                        rules={[
                            { required: true, message: 'Please enter a category code' },
                            { max: 50, message: 'Max 50 characters' },
                            { pattern: /^[A-Za-z0-9\-_]+$/, message: 'Only letters, numbers, hyphens and underscores' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Category Name" name="name"
                        rules={[
                            { required: true, message: 'Please enter a category name' },
                            { max: 150, message: 'Max 150 characters' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => { setIsModalVisible(false); editForm.resetFields(); }}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageSalesProductCategory;
